// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Api } from '../../services/config/Api';
import { useOrganizationId } from '../../state/hooks/OrganizationHooks';
import { useCurrentScenarioDatasetList, useCurrentScenarioData } from '../../state/hooks/ScenarioHooks';
import { WizardUtils } from '../../utils/WizardUtils';
import {
  dispatchAddTechVar,
  dispatchDeleteGisement,
  dispatchGetAgingLawsTwingraphQueriesResults,
  dispatchGetDataVectorTwingraphQueriesResult,
  dispatchInitializeGanttTwingraphQueries,
  dispatchInitializeGisementTwingraphQueries,
  dispatchResetTechnicalVars,
  dispatchSetAgingLawsQueriesResult,
  dispatchSetNewTableNamesMapping,
  dispatchSetWizardQueriesStatus,
  dispatchUpdateGisementData,
  dispatchUpdateTechnicalPolicy,
} from './WizardDispatchers';

export const useGetAgingLawsByScenario = (scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.agingLaws);
};

export const useTwingraphQueriesStatus = () => {
  return useSelector((state) => state.wizard.status);
};

export const useSetWizardQueriesStatus = () => {
  const dispatch = useDispatch();
  return useCallback(
    (status) => {
      dispatch(dispatchSetWizardQueriesStatus(status));
    },
    [dispatch]
  );
};

export const useGetAgingLawsTwingraphQueriesResults = (scenarioData) => {
  const dispatch = useDispatch();
  const agingLaws = useGetAgingLawsByScenario(scenarioData.id);
  const status = useTwingraphQueriesStatus();
  return useCallback(
    (datasetId) => {
      if (!agingLaws && status !== 'LOADING')
        dispatch(dispatchGetAgingLawsTwingraphQueriesResults(datasetId, scenarioData));
    },
    [agingLaws, status, dispatch, scenarioData]
  );
};
export const useSetAgingLawsQueriesResult = () => {
  const dispatch = useDispatch();
  return useCallback(
    (scenarioId, result, key) => dispatch(dispatchSetAgingLawsQueriesResult(scenarioId, result, key)),
    [dispatch]
  );
};
export const useGetEquipments = (scenarioId) => {
  const hierarchieEquipements = useGetHierarchieEquipements(scenarioId)?.map(
    (equipment) => equipment.NiveauHierarchique
  );
  const agingLaws = useGetAgingLawsByScenario(scenarioId);
  const equipments = {};
  hierarchieEquipements?.forEach((equipment) => {
    equipments[equipment] = agingLaws[equipment];
  });
  return equipments;
};

export const useGetEquipmentsList = (scenarioId) => {
  return useGetHierarchieEquipements(scenarioId)?.map((equipment) => equipment.NiveauHierarchique);
};

export const useGetHierarchieEquipements = (scenarioId) => {
  return useSelector((state) => state.wizard[scenarioId]?.agingLaws?.HierarchieEquipements);
};

export const useAddTechVar = () => {
  const dispatch = useDispatch();
  return useCallback(
    (techVar, scenarioId) => {
      dispatch(dispatchAddTechVar(techVar, scenarioId));
    },
    [dispatch]
  );
};

export const useGetTechVar = (techVar, scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalVars?.[techVar]);
};

export const useResetTechnicalVars = (scenarioId) => {
  const dispatch = useDispatch();
  const agingLaws = useGetAgingLawsByScenario(scenarioId);
  return useCallback(() => {
    if (agingLaws) dispatch(dispatchResetTechnicalVars(scenarioId));
  }, [agingLaws, dispatch, scenarioId]);
};

export const useTechnicalPolicies = (scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy);
};

export const useInitializeGanttTwingraphQueries = (scenarioData) => {
  const dispatch = useDispatch();
  const technicalPolicy = useTechnicalPolicies(scenarioData.id);
  const status = useTwingraphQueriesStatus();
  return useCallback(
    (datasetId) => {
      if (!technicalPolicy && status !== 'LOADING')
        dispatch(dispatchInitializeGanttTwingraphQueries(datasetId, scenarioData));
    },
    [dispatch, scenarioData, status, technicalPolicy]
  );
};

export const useGisements = (scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy?.gisements);
};

export const useReglesGestionByGisement = (gisement, scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy.gisements[gisement]?.ReglesGestion);
};

export const useCriteresDeclenchementByGisement = (gisement, scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy.gisements[gisement]?.CriteresDeclenchement);
};

export const useSequenceActionByGisement = (gisement, scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy.gisements[gisement]?.SequencesActions);
};

export const useCoutsEtDureesByGisement = (gisement, scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy.gisements[gisement]?.CoutsEtDureesActions);
};

export const useImpactsByGisement = (gisement, scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy.gisements[gisement]?.Impacts);
};

export const useFamilleEquipementInTechnicalPolicy = (scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy.familleEquipement);
};

export const useNiveauDePriorisation = (scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy?.niveauDePriorisation);
};

export const useAssetClassAttrsByGisement = (gisement, scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy?.gisements[gisement]?.ClasseActif[0].Attrs);
};

export const useClasseActifNiveauCoutByGisement = (gisement, scenarioId) => {
  return useSelector(
    (state) => state.wizard?.[scenarioId]?.technicalPolicy?.gisements[gisement]?.ClasseActif[0].NiveauCout
  );
};

export const useClasseActifAttributeValuesByGisement = (gisement, scenarioId) => {
  return useSelector(
    (state) => state.wizard?.[scenarioId]?.technicalPolicy?.gisements[gisement]?.ClasseActif[0].Values
  );
};

export const useAssetClassAttributesValuesWithDescendants = (gisement, scenarioId) => {
  const assetClassAttributes = useAssetClassAttrsByGisement(gisement, scenarioId);
  const hierarchyData = useAssetClassesList(scenarioId);
  const technicalPolicyData = useTechnicalPolicies(scenarioId);
  const organizationId = useOrganizationId();
  const [datasetId] = useCurrentScenarioDatasetList();
  const assetClassId = useMemo(() => {
    return technicalPolicyData?.gisements?.[gisement]?.tableNamesMapping?.ClasseActif;
  }, [technicalPolicyData, gisement]);

  const [depositCustomDataAttributesValues, setDepositCustomDataAttributesValues] = useState({});

  useEffect(() => {
    if (!assetClassAttributes || !hierarchyData || !gisement || !assetClassId) {
      setDepositCustomDataAttributesValues({});
      return;
    }

    async function loadDepositCustomDataAttributesValues() {
      const collectedDescendantsCustomAttributes = {};
      const descendants = WizardUtils.findChildren(hierarchyData, assetClassId);
      const isDepositClassGroupe = hierarchyData.some(
        (item) => item.NiveauHierarchique === assetClassId && item.TypeNiveau === 'Groupe'
      );
      if (!isDepositClassGroupe) {
        descendants.push(assetClassId);
      }

      const descendantEntries = await Promise.all(
        descendants.map(async (descendant) => {
          const descendantItem = hierarchyData.find((item) => (item.NiveauHierarchique || item.Id) === descendant);

          if (!descendantItem) return {};

          const { data: twingraphRawAssetClassData } = await Api.Datasets.twingraphQuery(organizationId, datasetId, {
            query: WizardUtils.buildMetadataQueryString(descendant),
          });

          const assetClassAttributesValues = WizardUtils.getFilteredMetadata(twingraphRawAssetClassData);

          return Object.keys(assetClassAttributesValues)
            .filter((key) => key.startsWith('CustomDataString'))
            .reduce((acc, key) => {
              acc[key] = assetClassAttributesValues[key];
              return acc;
            }, {});
        })
      );
      descendantEntries.forEach((arr) => {
        Object.keys(arr).forEach((key) => {
          if (arr[key]) {
            collectedDescendantsCustomAttributes[key] = Array.from(
              new Set([...(collectedDescendantsCustomAttributes[key] || []), ...arr[key]])
            );
          }
        });
      });
      setDepositCustomDataAttributesValues(collectedDescendantsCustomAttributes);
    }
    loadDepositCustomDataAttributesValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gisement, assetClassId, assetClassAttributes, organizationId, datasetId]);
  return depositCustomDataAttributesValues;
};

export const useAssetClassAttributesWithDescendants = (gisement, scenarioId) => {
  const assetClassAttributes = useAssetClassAttrsByGisement(gisement, scenarioId);
  const hierarchyData = useAssetClassesList(scenarioId);
  const technicalPolicyData = useTechnicalPolicies(scenarioId);
  const organizationId = useOrganizationId();
  const [datasetId] = useCurrentScenarioDatasetList();
  const scenarioData = useCurrentScenarioData();
  const assetClassId = useMemo(() => {
    return technicalPolicyData?.gisements?.[gisement]?.tableNamesMapping?.ClasseActif;
  }, [technicalPolicyData, gisement]);

  const assetClassAttributesParameter = useMemo(() => {
    const unparsedAssetClassAttributesParameter = scenarioData.parametersValues?.find(
      (parameter) => parameter.parameterId === 'asset_class_columns'
    );
    return unparsedAssetClassAttributesParameter?.value ? JSON.parse(unparsedAssetClassAttributesParameter.value) : {};
  }, [scenarioData.parametersValues]);

  const [depositCustomDataAttributes, setDepositCustomDataAttributes] = useState([]);

  useEffect(() => {
    if (!assetClassAttributes || !hierarchyData || !gisement || !assetClassId) {
      setDepositCustomDataAttributes([]);
      return;
    }

    async function loadDepositCustomDataAttributes() {
      const collectedDescendantsCustomAttributes = [];
      // check if AssetClass id is type Groupe in hierarchy
      const isDepositClassGroupe = hierarchyData.some(
        (item) => item.NiveauHierarchique === assetClassId && item.TypeNiveau === 'Groupe'
      );
      if (!isDepositClassGroupe) {
        collectedDescendantsCustomAttributes.push(
          ...(assetClassAttributesParameter?.[assetClassId]?.split(',') || assetClassAttributes)
        );
      }
      // Find descendants
      // Groups are already filtered out by findChildren
      const descendants = WizardUtils.findChildren(hierarchyData, assetClassId);
      const descendantEntries = await Promise.all(
        descendants.map(async (descendant) => {
          const descendantItem = hierarchyData.find((item) => (item.NiveauHierarchique || item.Id) === descendant);

          if (!descendantItem) return [];

          const { data: twingraphRawAssetClassData } = await Api.Datasets.twingraphQuery(organizationId, datasetId, {
            query: WizardUtils.buildReducedClasseActifQueryString(descendant),
          });

          const twingraphAssetClassAttributes = twingraphRawAssetClassData?.[0]?.Attrs ?? [];
          const assetClassScenarioParametersAttributes =
            assetClassAttributesParameter?.[descendant]?.split(',') || twingraphAssetClassAttributes;
          return assetClassScenarioParametersAttributes.filter((a) => a.startsWith('CustomData'));
        })
      );

      descendantEntries.forEach((arr) => collectedDescendantsCustomAttributes.push(...arr));
      const flattenedDescendantsCustomAttributes = [...new Set(collectedDescendantsCustomAttributes)];
      setDepositCustomDataAttributes(
        flattenedDescendantsCustomAttributes.filter((attr) => attr !== 'CustomDataNumber.AgeReel')
      );
    }
    loadDepositCustomDataAttributes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gisement, assetClassId, assetClassAttributes, organizationId, datasetId, scenarioData.parametersValues]);

  return WizardUtils.sortResourceListAlphabetically(depositCustomDataAttributes);
};

export const useActionsImposeesByGisement = (gisement, scenarioId) => {
  return useSelector(
    (state) => state.wizard?.[scenarioId]?.technicalPolicy.gisements[gisement]?.SequencesActionsImposees
  );
};

export const useTableNamesMappingByGisement = (scenarioId, selectedGisement) => {
  return useSelector(
    (state) => state.wizard?.[scenarioId]?.technicalPolicy?.gisements[selectedGisement]?.tableNamesMapping
  );
};

export const useGisementById = (scenarioId, selectedGisement) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy?.gisements[selectedGisement]);
};

export const useGestionActivites = (scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy?.gestionActivites);
};

export const useInitializeGisementTwingraphQueries = () => {
  const dispatch = useDispatch();
  return useCallback(
    (selectedGisementTablesMapping, datasetId, scenarioData) => {
      dispatch(dispatchInitializeGisementTwingraphQueries(selectedGisementTablesMapping, datasetId, scenarioData));
    },
    [dispatch]
  );
};
export const useAssetClassesList = (scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy?.hierarchieDesEquipements);
};

export const useUpdateGisementData = () => {
  const dispatch = useDispatch();
  return useCallback(
    (scenarioId, result, key, gisementName) =>
      dispatch(dispatchUpdateGisementData(scenarioId, result, key, gisementName)),
    [dispatch]
  );
};

export const useSetNewTableNamesMapping = () => {
  const dispatch = useDispatch();
  return useCallback(
    (scenarioId, gisement, tableNamesMapping) =>
      dispatch(dispatchSetNewTableNamesMapping(scenarioId, gisement, tableNamesMapping)),
    [dispatch]
  );
};

export const useDeleteGisement = () => {
  const dispatch = useDispatch();
  return useCallback(
    (scenarioId, gisementName) => dispatch(dispatchDeleteGisement(scenarioId, gisementName)),
    [dispatch]
  );
};

export const useUpdateTechnicalPolicy = () => {
  const dispatch = useDispatch();
  return useCallback(
    (scenarioId, key, data) => dispatch(dispatchUpdateTechnicalPolicy(scenarioId, key, data)),
    [dispatch]
  );
};

export const useEnvelopeBudgetaireInTechnicalPolicy = (scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy?.enveloppesBudgetaire);
};
export const useFamilleEquipementByAssetClass = (scenarioId, assetClass) => {
  const mapping = useSelector((state) => state.wizard?.[scenarioId]?.technicalPolicy?.equipmentFamiliesMapping);

  if (!mapping || !assetClass) return [];

  const result = mapping[assetClass] ?? [];

  if (typeof result[0] === 'string') {
    return result.map((item) => ({
      label: item,
      value: item,
    }));
  }

  return result;
};

export const useGetDataVectorListByScenario = (scenarioId) => {
  return useSelector((state) => state.wizard?.[scenarioId]?.dataVector);
};

export const useGetDataVectorTwingraphQueriesResults = (scenarioData) => {
  const dispatch = useDispatch();
  const dataVectorList = useGetDataVectorListByScenario(scenarioData.id);
  const status = useTwingraphQueriesStatus();
  return useCallback(
    (datasetId) => {
      if (!dataVectorList && status !== 'LOADING')
        dispatch(dispatchGetDataVectorTwingraphQueriesResult(datasetId, scenarioData));
    },
    [dataVectorList, status, dispatch, scenarioData]
  );
};
